import {BaseInput} from "@/components/BaseInput";
import {COMMON_COUNTRIES, DEVICE_OPTIONS, GENDER_OPTIONS, KYC_STATUSES} from "@/constants/push.constants";
import type {AudienceFilters, AudienceType, DeviceType, Gender} from "@/types/push.types";

interface Step2AudienceProps {
  formData: {
    audienceType: AudienceType;
    audienceFilters?: AudienceFilters;
    targetUserId?: string;
  };
  onChange: (data: Step2AudienceProps["formData"]) => void;
  errors: Record<string, string>;
}

export const Step2Audience = ({formData, onChange, errors}: Step2AudienceProps) => {
  const toggleCountry = (countryCode: string) => {
    const current = formData.audienceFilters?.countries ?? [];
    const newCountries = current.includes(countryCode)
      ? current.filter(c => c !== countryCode)
      : [...current, countryCode];

    onChange({
      ...formData,
      audienceFilters: {
        ...formData.audienceFilters,
        countries: newCountries.length > 0 ? newCountries : undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{color: "var(--color-text)"}}
        >
          Step 2: Audience
        </h2>
        <p
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          Define who should receive this notification
        </p>
      </div>

      {/* Audience Type */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{color: "var(--color-text)"}}
        >
          Who receives this notification? <span style={{color: "var(--color-danger)"}}>*</span>
        </label>
        <div className="flex flex-col gap-2">
          <label
            className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
            style={{
              borderColor: formData.audienceType === "all" ? "var(--color-primary)" : "var(--color-surface-2)",
              backgroundColor: formData.audienceType === "all" ? "var(--color-primary)10" : "transparent",
            }}
          >
            <input
              type="radio"
              name="audienceType"
              value="all"
              checked={formData.audienceType === "all"}
              onChange={e => onChange({...formData, audienceType: e.target.value as AudienceType})}
              className="shrink-0"
            />
            <div>
              <p
                className="text-sm font-medium"
                style={{color: "var(--color-text)"}}
              >
                All users
              </p>
              <p
                className="text-xs"
                style={{color: "var(--color-text-secondary)"}}
              >
                Send to every user with an active device
              </p>
            </div>
          </label>

          <label
            className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
            style={{
              borderColor: formData.audienceType === "segment" ? "var(--color-primary)" : "var(--color-surface-2)",
              backgroundColor: formData.audienceType === "segment" ? "var(--color-primary)10" : "transparent",
            }}
          >
            <input
              type="radio"
              name="audienceType"
              value="segment"
              checked={formData.audienceType === "segment"}
              onChange={e => onChange({...formData, audienceType: e.target.value as AudienceType})}
              className="shrink-0"
            />
            <div>
              <p
                className="text-sm font-medium"
                style={{color: "var(--color-text)"}}
              >
                Specific segment
              </p>
              <p
                className="text-xs"
                style={{color: "var(--color-text-secondary)"}}
              >
                Target users based on filters (country, KYC status, etc.)
              </p>
            </div>
          </label>

          <label
            className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
            style={{
              borderColor: formData.audienceType === "individual" ? "var(--color-primary)" : "var(--color-surface-2)",
              backgroundColor: formData.audienceType === "individual" ? "var(--color-primary)10" : "transparent",
            }}
          >
            <input
              type="radio"
              name="audienceType"
              value="individual"
              checked={formData.audienceType === "individual"}
              onChange={e => onChange({...formData, audienceType: e.target.value as AudienceType})}
              className="shrink-0"
            />
            <div>
              <p
                className="text-sm font-medium"
                style={{color: "var(--color-text)"}}
              >
                Individual user
              </p>
              <p
                className="text-xs"
                style={{color: "var(--color-text-secondary)"}}
              >
                Send to a specific user by their ID
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Individual User ID */}
      {formData.audienceType === "individual" && (
        <div>
          <label
            htmlFor="target-user"
            className="block text-sm font-medium mb-1.5"
            style={{color: "var(--color-text)"}}
          >
            User ID <span style={{color: "var(--color-danger)"}}>*</span>
          </label>
          <BaseInput
            id="target-user"
            type="text"
            placeholder="Enter user UUID"
            value={formData.targetUserId ?? ""}
            onChange={e => onChange({...formData, targetUserId: e.target.value})}
            error={errors.targetUserId}
          />
        </div>
      )}

      {/* Segment Filters */}
      {formData.audienceType === "segment" && (
        <div
          className="flex flex-col gap-4 p-4 rounded-lg"
          style={{backgroundColor: "var(--color-surface-2)"}}
        >
          <p
            className="text-sm font-medium"
            style={{color: "var(--color-text)"}}
          >
            Segment Filters (all filters are optional)
          </p>

          {/* Countries */}
          <div>
            <label
              className="block text-xs font-medium mb-2"
              style={{color: "var(--color-text)"}}
            >
              Countries
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {COMMON_COUNTRIES.map(country => (
                <label
                  key={country.code}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.audienceFilters?.countries?.includes(country.code) ?? false}
                    onChange={() => toggleCountry(country.code)}
                  />
                  <span style={{color: "var(--color-text)"}}>{country.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* KYC Status */}
          <div>
            <label
              htmlFor="kyc-status"
              className="block text-xs font-medium mb-1.5"
              style={{color: "var(--color-text)"}}
            >
              KYC Status
            </label>
            <select
              id="kyc-status"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                backgroundColor: "var(--color-bg)",
                border: "1px solid var(--color-surface-2)",
                color: "var(--color-text)",
              }}
              value={formData.audienceFilters?.kyc_status ?? ""}
              onChange={e =>
                onChange({
                  ...formData,
                  audienceFilters: {
                    ...formData.audienceFilters,
                    kyc_status: e.target.value || undefined,
                  },
                })
              }
            >
              <option value="">Any</option>
              {KYC_STATUSES.map(status => (
                <option
                  key={status.value}
                  value={status.value}
                >
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label
              htmlFor="gender"
              className="block text-xs font-medium mb-1.5"
              style={{color: "var(--color-text)"}}
            >
              Gender
            </label>
            <select
              id="gender"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                backgroundColor: "var(--color-bg)",
                border: "1px solid var(--color-surface-2)",
                color: "var(--color-text)",
              }}
              value={formData.audienceFilters?.gender ?? ""}
              onChange={e =>
                onChange({
                  ...formData,
                  audienceFilters: {
                    ...formData.audienceFilters,
                    gender: e.target.value ? (e.target.value as Gender) : undefined,
                  },
                })
              }
            >
              <option value="">Any</option>
              {GENDER_OPTIONS.map(option => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Device */}
          <div>
            <label
              htmlFor="device"
              className="block text-xs font-medium mb-1.5"
              style={{color: "var(--color-text)"}}
            >
              Device
            </label>
            <select
              id="device"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                backgroundColor: "var(--color-bg)",
                border: "1px solid var(--color-surface-2)",
                color: "var(--color-text)",
              }}
              value={formData.audienceFilters?.device ?? ""}
              onChange={e =>
                onChange({
                  ...formData,
                  audienceFilters: {
                    ...formData.audienceFilters,
                    device: e.target.value ? (e.target.value as DeviceType) : undefined,
                  },
                })
              }
            >
              <option value="">Any</option>
              {DEVICE_OPTIONS.map(option => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Has Wallet */}
          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.audienceFilters?.has_wallet ?? false}
                onChange={e =>
                  onChange({
                    ...formData,
                    audienceFilters: {
                      ...formData.audienceFilters,
                      has_wallet: e.target.checked || undefined,
                    },
                  })
                }
              />
              <span style={{color: "var(--color-text)"}}>Only users with active wallets</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
